/**
 * Hari Pujan — storefront behaviour. Loaded on every template (snippets/stylesheets.liquid).
 *
 *  1. Motion: sections and cards fade up as they enter the screen.
 *  2. <hp-scroller>: swipe rows with arrows and a progress line.
 *  3. <hp-hero>: premium hero slideshow (sections/hp-hero.liquid).
 *  4. <hp-tabs>: tabbed product grid (sections/hp-product-tabs.liquid).
 *  5. Product page gallery auto-advance.
 *  6. Sticky "Buy now" that presses the page's real Buy now button.
 *
 * Everything that moves is off under prefers-reduced-motion and in the theme editor.
 */

const hpReduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * 1. Motion. Elements marked [data-hp-reveal] start slightly lowered and transparent and
 * settle in once on screen; --hp-i staggers siblings. The hidden start state only applies
 * under html.hp-motion, which is set here, so content stays visible if this script fails.
 * Horizon's product grids (collection, search, recommendations) are tagged automatically.
 */
(() => {
  if (hpReduceMotion() || (window.Shopify && window.Shopify.designMode) || !('IntersectionObserver' in window)) return;
  document.documentElement.classList.add('hp-motion');

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
  );

  const scan = (root = document) => {
    root.querySelectorAll('.product-grid__item, .resource-list--grid .resource-list__item').forEach((el, i) => {
      if (el.hasAttribute('data-hp-reveal')) return;
      el.setAttribute('data-hp-reveal', '');
      el.style.setProperty('--hp-i', String(i % 4));
    });
    root.querySelectorAll('[data-hp-reveal]:not(.is-in)').forEach((el) => io.observe(el));
  };

  const start = () => {
    scan();
    // Filters, sorting and "load more" replace grid items: tag and observe the new ones.
    const main = document.querySelector('main') || document.body;
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        scan(main);
      });
    }).observe(main, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/**
 * 2. <hp-scroller> — a horizontal row ([data-hp-track]) with optional prev/next buttons
 * ([data-hp-prev] / [data-hp-next]) and a progress line ([data-hp-progress]).
 * Adds .has-overflow when the row is wider than its box. update() is public so other
 * components (tabs) can refresh it after showing or hiding items.
 */
class HpScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hp-track]');
    if (!this.track || this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.prev = this.querySelector('[data-hp-prev]');
    this.next = this.querySelector('[data-hp-next]');
    this.bar = this.querySelector('[data-hp-progress]');

    let frame = 0;
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => this.update());
    }, { passive: true, signal });

    const step = () => Math.max(this.track.clientWidth * 0.85, 160);
    const behavior = hpReduceMotion() ? 'auto' : 'smooth';
    this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior }), { signal });
    this.next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior }), { signal });

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);
    this.update();
    this.autoRotate(signal);
  }

  /** Card rows move one card every 4 s and loop. Pauses on hover, focus and touch (resumes
   * 6 s after), off screen and in background tabs. Not for reels, reduced motion or the editor. */
  autoRotate(signal) {
    if (hpReduceMotion() || window.Shopify?.designMode || this.matches('.hp-reels__row')) return;
    let paused = false;
    let visible = false;
    let resume;
    const pause = () => { paused = true; clearTimeout(resume); };
    const later = () => { clearTimeout(resume); resume = setTimeout(() => (paused = false), 6000); };
    this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') pause(); }, { signal });
    this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') paused = false; }, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', later, { signal });
    this.track.addEventListener('touchstart', pause, { passive: true, signal });
    this.track.addEventListener('touchend', later, { passive: true, signal });
    const io = new IntersectionObserver(([e]) => (visible = e.isIntersecting), { threshold: 0.4 });
    io.observe(this);
    signal.addEventListener('abort', () => io.disconnect());
    const timer = setInterval(() => {
      if (paused || !visible || document.hidden || !this.classList.contains('has-overflow')) return;
      const card = [...this.track.children].find((c) => !c.hidden);
      if (!card) return;
      const step = card.getBoundingClientRect().width + (parseFloat(getComputedStyle(this.track).columnGap) || 0);
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
      this.track.scrollTo({ left: atEnd ? 0 : this.track.scrollLeft + step, behavior: 'smooth' });
    }, 4000);
    signal.addEventListener('abort', () => clearInterval(timer));
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.resizeObserver?.disconnect();
  }

  update() {
    if (!this.track) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const overflow = max > 4;
    const left = Math.abs(this.track.scrollLeft);
    this.classList.toggle('has-overflow', overflow);
    if (this.bar) {
      this.bar.style.setProperty('--hp-progress-size', overflow ? (this.track.clientWidth / this.track.scrollWidth).toFixed(3) : '1');
      this.bar.style.setProperty('--hp-progress-pos', overflow ? (left / max).toFixed(3) : '0');
    }
    if (this.prev) this.prev.disabled = !overflow || left < 4;
    if (this.next) this.next.disabled = !overflow || left > max - 4;
  }
}
if (!customElements.get('hp-scroller')) customElements.define('hp-scroller', HpScroller);

/**
 * 3. <hp-hero> — slides stack in one grid cell; .is-active fades in. The active progress
 * segment's fill animation (CSS, --hp-hero-interval) drives autoplay: when it ends, the next
 * slide shows. Pausing = pausing that animation (.is-playing toggles animation-play-state),
 * so progress resumes where it stopped. Pauses on hover, focus, touch (resumes 5 s after),
 * off screen, in background tabs, while a slide is selected in the theme editor, and when
 * the shopper presses pause. Swipe left/right on touch.
 */
class HpHero extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.slides = [...this.querySelectorAll('.hp-hero__slide')];
    if (this.slides.length < 2) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.dots = [...this.querySelectorAll('[data-hp-go]')];
    this.current = this.querySelector('[data-hp-current]');
    this.pauseButton = this.querySelector('[data-hp-pause]');
    this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
    this.autoplay = this.dataset.autoplay === 'true' && !hpReduceMotion();
    this.state = { hover: false, focus: false, touch: false, user: false, editor: false, visible: true };

    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.hpGo)), { signal }));
    this.pauseButton?.addEventListener('click', () => {
      this.state.user = !this.state.user;
      this.pauseButton.setAttribute('aria-pressed', String(this.state.user));
      this.pauseButton.setAttribute('aria-label', this.state.user ? 'Play slideshow' : 'Pause slideshow');
      this.sync();
    }, { signal });

    this.addEventListener('animationend', (event) => {
      if (event.animationName !== 'hp-hero-fill') return;
      if (!event.target.closest('.hp-hero__dot.is-active')) return;
      this.go(this.index + 1);
    }, { signal });

    this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.set('hover', true); }, { signal });
    this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.set('hover', false); }, { signal });
    this.addEventListener('focusin', () => this.set('focus', true), { signal });
    this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.set('focus', false); }, { signal });

    let startX = null;
    let startY = null;
    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      startX = e.clientX;
      startY = e.clientY;
      clearTimeout(this.resumeTimer);
      this.set('touch', true);
    }, { signal });
    const end = (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      startX = null;
      if (e.type === 'pointerup' && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1));
      this.resumeTimer = setTimeout(() => this.set('touch', false), 5000);
    };
    this.addEventListener('pointerup', end, { signal });
    this.addEventListener('pointercancel', end, { signal });

    this.observer = new IntersectionObserver(([entry]) => this.set('visible', entry.isIntersecting), { threshold: 0.2 });
    this.observer.observe(this);
    document.addEventListener('visibilitychange', () => this.sync(), { signal });

    document.addEventListener('shopify:block:select', (e) => {
      const i = this.slides.indexOf(e.target);
      if (i < 0) return;
      this.go(i);
      this.set('editor', true);
    }, { signal });
    document.addEventListener('shopify:block:deselect', () => this.set('editor', false), { signal });

    this.sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.observer?.disconnect();
    clearTimeout(this.resumeTimer);
  }

  set(key, value) {
    this.state[key] = value;
    this.sync();
  }

  sync() {
    const s = this.state;
    const playing = this.autoplay && !s.hover && !s.focus && !s.touch && !s.user && !s.editor && s.visible && !document.hidden;
    this.classList.toggle('is-playing', Boolean(playing));
  }

  go(target) {
    const count = this.slides.length;
    const next = ((target % count) + count) % count;
    if (next === this.index) return;
    const from = this.slides[this.index];
    const to = this.slides[next];
    // Make sure the incoming photo is requested before it fades in.
    to.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    from.classList.remove('is-active');
    from.setAttribute('aria-hidden', 'true');
    from.inert = true;
    to.classList.add('is-active');
    to.removeAttribute('aria-hidden');
    to.inert = false;
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === next);
      dot.setAttribute('aria-current', i === next ? 'true' : 'false');
    });
    if (this.current) this.current.textContent = String(next + 1).padStart(2, '0');
    this.index = next;
    // Warm the slide after this one too.
    this.slides[(next + 1) % count].querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
  }
}
if (!customElements.get('hp-hero')) customElements.define('hp-hero', HpHero);

/**
 * 4. <hp-tabs> — chips ([role="tab"]) filter one list of cards. Each card <li> lists its
 * collections in data-collections (" handle-a handle-b "); "all" shows the first N.
 * Updates the "View all" link and resets the row's scroll. Arrow keys move between tabs.
 */
class HpTabs extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.max = Number(this.dataset.max) || 8;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.items = [...this.querySelectorAll('[data-collections]')];
    this.viewAll = this.querySelector('[data-tabs-viewall]');
    this.viewAllText = this.querySelector('[data-tabs-viewall-text]');
    this.viewAllPrefix = this.viewAllText?.dataset.prefix || '';
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab), { signal }));
    this.querySelector('[role="tablist"]')?.addEventListener('keydown', (e) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
      e.preventDefault();
      const i = this.tabs.indexOf(document.activeElement);
      let n = i;
      if (e.key === 'ArrowRight') n = (i + 1) % this.tabs.length;
      if (e.key === 'ArrowLeft') n = (i - 1 + this.tabs.length) % this.tabs.length;
      if (e.key === 'Home') n = 0;
      if (e.key === 'End') n = this.tabs.length - 1;
      this.tabs[n].focus();
      this.select(this.tabs[n]);
    }, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }

  select(tab) {
    this.tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    });
    const key = tab.dataset.tab;
    let shown = 0;
    for (const item of this.items) {
      const match = key === 'all' || item.dataset.collections.includes(` ${key} `);
      const show = match && shown < this.max;
      if (show) shown += 1;
      item.hidden = !show;
      if (show) item.classList.add('is-in');
    }
    if (this.viewAll) this.viewAll.href = tab.dataset.url;
    if (this.viewAllText) this.viewAllText.textContent = `${this.viewAllPrefix} ${tab.dataset.label}`.trim();
    const scroller = this.querySelector('hp-scroller');
    const track = scroller?.querySelector('[data-hp-track]');
    if (track) track.scrollLeft = 0;
    scroller?.update?.();
  }
}
if (!customElements.get('hp-tabs')) customElements.define('hp-tabs', HpTabs);

/**
 * 4b. <hp-tablist> — product info tabs (blocks/hp-pdp-tabs.liquid). [role="tab"] buttons
 * point at their panels with aria-controls; the first tab renders selected. Arrow keys,
 * Home and End move between tabs. A long description folds behind "Read more".
 */
class HpTablist extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab), { signal }));
    this.querySelector('[role="tablist"]')?.addEventListener('keydown', (e) => {
      const i = this.tabs.indexOf(document.activeElement);
      if (i < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
      e.preventDefault();
      const last = this.tabs.length - 1;
      const n = { ArrowRight: i === last ? 0 : i + 1, ArrowLeft: i === 0 ? last : i - 1, Home: 0, End: last }[e.key];
      this.tabs[n].focus();
      this.select(this.tabs[n]);
    }, { signal });
    this.querySelectorAll('[data-hp-fold]').forEach((panel) => this.fold(panel, signal));
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }

  select(tab) {
    for (const t of this.tabs) {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      const panel = this.querySelector(`#${CSS.escape(t.getAttribute('aria-controls'))}`);
      if (panel) panel.hidden = !on;
    }
    this.querySelectorAll('[data-hp-fold]:not([hidden])').forEach((panel) => this.measure(panel));
  }

  fold(panel, signal) {
    const toggle = panel.querySelector('[data-hp-fold-toggle]');
    if (!toggle) return;
    toggle.addEventListener('click', () => {
      const open = panel.classList.toggle('is-folded') === false;
      toggle.setAttribute('aria-expanded', String(open));
      toggle.querySelector('[data-more]').hidden = open;
      toggle.querySelector('[data-less]').hidden = !open;
    }, { signal });
    this.measure(panel);
  }

  /** Fold only when the text is clearly longer than the folded height (15em ≈ 240px). */
  measure(panel) {
    const body = panel.querySelector('[data-hp-fold-body]');
    const toggle = panel.querySelector('[data-hp-fold-toggle]');
    if (!body || !toggle || panel.hidden || panel.dataset.measured) return;
    panel.dataset.measured = 'true';
    const long = body.scrollHeight > 320;
    panel.classList.toggle('is-folded', long);
    toggle.hidden = !long;
  }
}
if (!customElements.get('hp-tablist')) customElements.define('hp-tablist', HpTablist);

/**
 * 4c. <hp-recently-viewed> — keeps the last 12 product handles this shopper viewed in
 * localStorage ('hp:recent'), adds the current product, then renders the others from each
 * product's live JSON. Products that no longer exist are dropped. Stays hidden when empty.
 */
class HpRecentlyViewed extends HTMLElement {
  async connectedCallback() {
    if (this.started) return;
    this.started = true;
    const KEY = 'hp:recent';
    const current = this.dataset.current;
    const limit = Number(this.dataset.limit) || 8;
    let list = [];
    try {
      list = JSON.parse(localStorage.getItem(KEY) || '[]').filter((h) => typeof h === 'string');
    } catch {}
    const others = list.filter((h) => h && h !== current).slice(0, limit);
    try {
      localStorage.setItem(KEY, JSON.stringify([current, ...list.filter((h) => h !== current)].filter(Boolean).slice(0, 12)));
    } catch {}
    if (!others.length) return;

    const results = await Promise.all(others.map(async (handle) => {
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`);
        return res.ok ? await res.json() : { gone: handle };
      } catch {
        return null;
      }
    }));
    const gone = results.filter((r) => r && r.gone).map((r) => r.gone);
    if (gone.length) {
      try {
        localStorage.setItem(KEY, JSON.stringify(JSON.parse(localStorage.getItem(KEY) || '[]').filter((h) => !gone.includes(h))));
      } catch {}
    }
    const products = results.filter((r) => r && !r.gone && r.url);
    const listEl = this.querySelector('[data-hp-rv-list]');
    if (!products.length || !listEl) return;

    listEl.replaceChildren(...products.map((p) => this.card(p)));
    this.hidden = false;
    this.querySelector('hp-scroller')?.update?.();
  }

  card(p) {
    const li = document.createElement('li');
    li.className = 'hp-row__item';
    const a = document.createElement('a');
    a.className = 'hp-rv__card';
    a.href = p.url;
    const media = document.createElement('span');
    media.className = 'hp-rv__img';
    if (p.featured_image) {
      const img = document.createElement('img');
      const src = p.featured_image.startsWith('//') ? `https:${p.featured_image}` : p.featured_image;
      img.src = `${src}${src.includes('?') ? '&' : '?'}width=480`;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 480;
      img.height = 600;
      media.append(img);
    }
    const title = document.createElement('span');
    title.className = 'hp-rv__title';
    title.textContent = p.title;
    const price = document.createElement('span');
    price.className = 'hp-rv__price';
    price.textContent = this.money(p.price);
    if (p.compare_at_price && p.compare_at_price > p.price) {
      const was = document.createElement('s');
      was.textContent = this.money(p.compare_at_price);
      price.append(' ', was);
    }
    a.append(media, title, price);
    li.append(a);
    return li;
  }

  /** Formats paise with the shop's money format ({{amount}}, {{amount_no_decimals}}, …). */
  money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const value = Number(cents) / 100;
    const group = (n, decimals, thousands, point) => {
      const [whole, frac] = n.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? point + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return group(value, 0, ',', '.');
      if (key === 'amount_with_comma_separator') return group(value, 2, '.', ',');
      if (key === 'amount_no_decimals_with_comma_separator') return group(value, 0, '.', ',');
      return group(value, 2, ',', '.');
    });
  }
}
if (!customElements.get('hp-recently-viewed')) customElements.define('hp-recently-viewed', HpRecentlyViewed);

/**
 * 5. Product page photo gallery moves to the next photo every 4s, looping back to the first.
 * Stops for good once the shopper touches, clicks, swipes or tabs into the gallery; pauses on
 * hover and while off screen. Uses the gallery's own slideshow (public next()/select()), so
 * arrows, thumbnails and zoom keep working as normal. Off for reduced motion.
 */
(() => {
  if (hpReduceMotion()) return;

  const INTERVAL = 4000;

  function start(gallery) {
    const slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow || slideshow.dataset.hpRotate || slideshow.closest('dialog')) return;
    if (gallery.querySelectorAll('slideshow-slide').length < 2) return;
    slideshow.dataset.hpRotate = 'true';

    let stopped = false;
    let visible = false;
    const stop = () => (stopped = true);
    for (const type of ['pointerdown', 'touchstart', 'keydown', 'wheel']) {
      gallery.addEventListener(type, stop, { passive: true });
    }
    new IntersectionObserver(([entry]) => (visible = entry.isIntersecting), { threshold: 0.5 }).observe(gallery);

    const timer = setInterval(() => {
      if (stopped) return clearInterval(timer);
      if (!visible || document.hidden || gallery.matches(':hover')) return;
      if (typeof slideshow.next !== 'function') return clearInterval(timer);
      if (slideshow.atEnd) slideshow.select(0);
      else slideshow.next();
    }, INTERVAL);
  }

  const init = () => document.querySelectorAll('media-gallery').forEach(start);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/**
 * 6. Sticky "Buy now" on the product page's sticky add-to-cart bar.
 * It never builds its own checkout: it presses the page's real Buy now button, so checkout,
 * attribution and the gateway keep working exactly as before. Since Oct 2026 the store uses
 * Shiprocket Fastrr, which draws its own BUY NOW (.shiprocket-headless .sr-headless-checkout)
 * inside .accelerated-checkout-block; that button is pressed first. Shopify's own payment
 * button is the fallback. Only a visible, enabled button is ever pressed.
 * Fail-safe: the sticky Buy now stays hidden whenever no such button exists (still loading,
 * sold out, or accelerated checkout off). Double-tap guard 1.2 s.
 */
(() => {
  const start = () => {
    const bar = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__bar');
    if (!bar || bar.dataset.hpBuyNow) return;
    bar.dataset.hpBuyNow = 'true';
    const section = bar.closest('.shopify-section') || document;
    const shown = (el) => Boolean(el) && !el.disabled && el.getClientRects().length > 0;

    function findRealButton() {
      const block = section.querySelector('.accelerated-checkout-block:not([hidden])');
      if (!block) return null;
      const shiprocket = block.querySelector('.shiprocket-headless .sr-headless-checkout');
      if (shown(shiprocket)) return shiprocket;
      const direct = block.querySelector('.shopify-payment-button__button');
      if (shown(direct)) return direct;
      for (const el of block.querySelectorAll('*')) {
        const inner = el.shadowRoot && el.shadowRoot.querySelector('button:not([disabled])');
        if (shown(inner)) return inner;
      }
      return [...block.querySelectorAll('button:not([disabled])')].find(shown) || null;
    }

    const buy = document.createElement('button');
    buy.type = 'button';
    buy.className = 'hp-sticky-buynow';
    buy.textContent = 'Buy now';
    buy.hidden = true;

    let locked = false;
    buy.addEventListener('click', () => {
      if (locked) return;
      const real = findRealButton();
      if (!real) return refresh();
      locked = true;
      real.click();
      setTimeout(() => (locked = false), 1200);
    });

    function refresh() {
      if (!buy.isConnected || buy.parentElement !== bar) bar.appendChild(buy);
      const ok = Boolean(findRealButton());
      buy.hidden = !ok;
      bar.classList.toggle('hp-has-buynow', ok);
      return ok;
    }

    // The gateway draws its button after load: check for up to 10 s.
    let tries = 0;
    (function poll() {
      if (!refresh() && tries++ < 20) setTimeout(poll, 500);
    })();

    // Variant changes re-render parts of the section: re-check (debounced).
    let pending;
    new MutationObserver(() => {
      clearTimeout(pending);
      pending = setTimeout(refresh, 250);
    }).observe(section, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
